'use client';

import { type ReactElement } from 'react';

import { useAnalytics } from '@/hooks/analytics';
import { useIsMobile } from '@/hooks/ui/useIsMobile';

import AuthForm from '../../AuthForm';
import { useScreenDidMount } from '../../hooks';
import { useSisu } from '../../Provider';
import { TwoColumnWrapper, ValuePropsListWrapper } from '../../styles';
import { AccountPageWrap, AuthFormWrapper } from './styles';

export type AccountScreenProps = {
	/**
	 * @defaultValue
	 * ```tsx
	 * "sisu-account"
	 * ```
	 */
	'data-testid'?: string;

	/**
	 * @defaultValue
	 * ```tsx
	 * <AuthForm />
	 * ```
	 */
	authForm?: ReactElement;

	/**
	 * @defaultValue
	 * ```tsx
	 * null
	 * ```
	 */
	branding?: ReactElement | null;

	/**
	 * @defaultValue
	 * ```tsx
	 * "left"
	 * ```
	 */
	brandingPosition?: 'left' | 'right';

	/**
	 * ```tsx
	 * null
	 * ```
	 */
	contentBasedImage?: ReactElement | null;

	/**
	 * ```tsx
	 * null
	 * ```
	 */
	header?: ReactElement | null;

	/**
	 * ```tsx
	 * null
	 * ```
	 */
	valuePropsList?: ReactElement | null;

	onScreenDidMount?: () => void;
};

export default function AccountScreen({
	'data-testid': testId = 'sisu-account',

	authForm = <AuthForm />,

	branding = null,
	brandingPosition = 'left',

	contentBasedImage = null,

	header = null,

	valuePropsList = null,

	onScreenDidMount,
}: AccountScreenProps) {
	const { logEvent } = useAnalytics();
	const { activeScreen, backgroundColor, flowName, isWithinModal, logPageViewedEvent } = useSisu();
	const [isMobile] = useIsMobile();

	useScreenDidMount('account', () => {
		logPageViewedEvent('account');
		logEvent({
			eventName: 'Login Form : Landed',
			eventProps: {
				source: flowName,
			},
		});
		onScreenDidMount?.();
	});

	if (activeScreen !== 'account') {
		return null;
	}

	return (
		<>
			{!branding && header}
			<AccountPageWrap
				backgroundColor={backgroundColor}
				data-testid={testId}
				isCompact={isWithinModal}
				isWithinModal={isWithinModal || !valuePropsList}
			>
				<TwoColumnWrapper isWithinModal={isWithinModal}>
					<AuthFormWrapper>
						{((brandingPosition === 'right' && !isMobile) || (isMobile && !isWithinModal)) &&
							!header &&
							branding}
						{authForm}
					</AuthFormWrapper>
					{valuePropsList && (
						<ValuePropsListWrapper isWithinModal={isWithinModal} showHeader={!!header}>
							{((brandingPosition === 'left' && !isMobile) || (isMobile && isWithinModal)) &&
								!header &&
								branding}
							{contentBasedImage}
							{valuePropsList}
						</ValuePropsListWrapper>
					)}
				</TwoColumnWrapper>
			</AccountPageWrap>
		</>
	);
}
